import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { cn } from '../../utils/cn'
import PropTypes from 'prop-types'
import {
  BarChart3,
  TrendingUp,
  LineChart,
  Package,
  Truck,
  Users,
  Tag,
  FileText,
  Warehouse,
  LogOut,
  Menu,
  UserCog,
  X,
  Coffee,
  Bell,
  User,
  ChevronDown,
  ChevronRight,
  ShoppingCart,
  Clipboard,
  Shield,
  Plus,
  Eye,
  FileCheck,
  Package2,
  ArrowRightLeft,
  Monitor,
  DollarSign,
  Settings
} from 'lucide-react'
import BranchSelectionModal from '../modals/BranchSelectionModal'
import { hasPermission, PERMISSIONS, isManagerRole } from '../../config/roles'
import BranchService from '../../services/branchService'
import { getCurrentDateFormatted } from '../../utils/timezone'

const sidebarItems = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: BarChart3,
    path: '/admin/dashboard',
    requiredPermissions: [PERMISSIONS.DASHBOARD.VIEW]
  },
  {
    id: 'sales',
    label: 'Sales',
    icon: TrendingUp,
    hasDropdown: true,
    requiredPermissions: [PERMISSIONS.REPORTS.VIEW],
    children: [
      {
        id: 'all-sales',
        label: 'All Sales Transactions',
        path: '/admin/sales',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      },
      {
        id: 'sales-summary',
        label: 'Sales Summary',
        path: '/admin/reports/sales-summary',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      },
      {
        id: 'product-wise-sales',
        label: 'Product-Wise Sales',
        path: '/admin/reports/product-wise-sales',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      },
      {
        id: 'payments-report',
        label: 'Payment Methods',
        path: '/admin/reports/payments',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      },
      {
        id: 'returns-report',
        label: 'Customer Returns',
        path: '/admin/reports/customer-returns',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      }
    ]
  },
  {
    id: 'analytics',
    label: 'Analytics & Theft',
    icon: LineChart,
    hasDropdown: true,
    requiredPermissions: [PERMISSIONS.REPORTS.VIEW],
    children: [
      {
        id: 'analytics-dashboard',
        label: 'Analytics Dashboard',
        path: '/admin/analytics',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      },
      {
        id: 'product-profitability',
        label: 'Product Profitability',
        path: '/admin/analytics/product-profitability',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      },
      {
        id: 'recipe-variance',
        label: 'Recipe & Theft Variance',
        path: '/admin/analytics/recipe-variance',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      },
      {
        id: 'consumption-report',
        label: 'Raw Material Consumption Report',
        path: '/admin/analytics/consumption-report',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      },
      {
        id: 'recipe-calculator',
        label: 'Recipe & Product Calculator',
        path: '/admin/analytics/calculator',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      },
      {
        id: 'manual-wastage',
        label: 'Manual Wastage Entry',
        path: '/admin/analytics/wastage',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      },
    ]
  },
  {
    id: 'products',
    label: 'Products',
    icon: Package,
    hasDropdown: true,
    requiredPermissions: [PERMISSIONS.PRODUCTS.VIEW],
    children: [
      { 
        id: 'category', 
        label: 'Category', 
        path: '/admin/products/category',
        requiredPermissions: [PERMISSIONS.PRODUCTS.MANAGE_CATEGORIES]
      },
      { 
        id: 'units', 
        label: 'Units', 
        path: '/admin/products/units',
        requiredPermissions: [PERMISSIONS.PRODUCTS.MANAGE_UNITS]
      },
      { 
        id: 'all-products', 
        label: 'All Products', 
        path: '/admin/products',
        requiredPermissions: [PERMISSIONS.PRODUCTS.VIEW],
        excludeRoles: ['manager', 'warehouse'] // Hide All Products from managers and warehouse users
      },
      { 
        id: 'products-inventory', 
        label: 'Products Inventory', 
        path: '/admin/products/products-inventory',
        requiredPermissions: [PERMISSIONS.PRODUCTS.VIEW_INVENTORY]
      },
      { 
        id: 'raw-materials', 
        label: 'Raw Materials', 
        path: '/admin/products/raw-materials',
        requiredPermissions: [PERMISSIONS.PRODUCTS.MANAGE_RAW_MATERIALS]
      },
      { 
        id: 'raw-material-inventory', 
        label: 'Raw Material Inventory', 
        path: '/admin/products/raw-materials/inventory',
        requiredPermissions: [PERMISSIONS.PRODUCTS.VIEW_RAW_INVENTORY]
      },
      { 
        id: 'recipes', 
        label: 'Recipes', 
        path: '/admin/products/recipes',
        requiredPermissions: [PERMISSIONS.PRODUCTS.MANAGE_RECIPES]
      },
      { 
        id: 'stock-alerts', 
        label: 'Stock Alerts', 
        path: '/admin/products/stock-alerts',
        requiredPermissions: [PERMISSIONS.PRODUCTS.VIEW_STOCK_ALERTS]
      },
      { 
        id: 'batch-inventory', 
        label: 'Batch Inventory Management', 
        path: '/admin/products/batch-inventory',
        requiredPermissions: [PERMISSIONS.PRODUCTS.VIEW_INVENTORY]
      },
      { 
        id: 'vip-rooms', 
        label: 'VIP Rooms', 
        path: '/admin/products/vip-rooms',
        requiredPermissions: [PERMISSIONS.PRODUCTS.VIEW],
        excludeRoles: ['warehouse'] // Hide VIP Rooms from warehouse users
      },
      { 
        id: 'tables', 
        label: 'Tables', 
        path: '/admin/tables',
        requiredPermissions: [PERMISSIONS.TABLES.VIEW]
      },
    ]
  },
  {
    id: 'suppliers',
    label: 'Suppliers',
    icon: Truck,
    path: '/admin/suppliers',
    requiredPermissions: [PERMISSIONS.SUPPLIERS.VIEW]
  },
  {
    id: 'customers',
    label: 'Customers',
    icon: Users,
    hasDropdown: true,
    requiredPermissions: [PERMISSIONS.CUSTOMERS.VIEW_ALL, PERMISSIONS.CUSTOMERS.VIEW_BRANCH],
    children: [
      { 
        id: 'all-customers', 
        label: 'All Customers', 
        path: '/admin/customers',
        requiredPermissions: [PERMISSIONS.CUSTOMERS.VIEW_ALL, PERMISSIONS.CUSTOMERS.VIEW_BRANCH]
      },
      { 
        id: 'credit-customers', 
        label: 'Credit Customers', 
        path: '/admin/customers/credit-customers',
        requiredPermissions: [PERMISSIONS.CUSTOMERS.VIEW_ALL, PERMISSIONS.CUSTOMERS.VIEW_BRANCH]
      },
      { 
        id: 'customers-group', 
        label: 'Customers Group', 
        path: '/admin/customers/customers-group',
        requiredPermissions: [PERMISSIONS.CUSTOMERS.MANAGE_GROUPS]
      },
    ]
  },
  {
    id: 'roles-permissions',
    label: 'Permissions',
    icon: Shield,
    hasDropdown: true,
    requiredPermissions: [PERMISSIONS.USERS.VIEW, PERMISSIONS.BRANCHES.VIEW],
    children: [
      { 
        id: 'branches', 
        label: 'Branches', 
        path: '/admin/roles/branches',
        requiredPermissions: [PERMISSIONS.BRANCHES.VIEW]
      },
      { 
        id: 'users', 
        label: 'Users', 
        path: '/admin/roles/users',
        requiredPermissions: [PERMISSIONS.USERS.VIEW]
      },
    ]
  },
  {
    id: 'purchase-order',
    label: 'Purchase Order',
    icon: ShoppingCart,
    hasDropdown: true,
    requiredPermissions: [PERMISSIONS.PURCHASE_ORDERS.VIEW],
    children: [
      { 
        id: 'create-po', 
        label: 'Create PO', 
        path: '/admin/purchase-order/create',
        requiredPermissions: [PERMISSIONS.PURCHASE_ORDERS.CREATE]
      },
      { 
        id: 'all-po', 
        label: 'All PO', 
        path: '/admin/purchase-order/all',
        requiredPermissions: [PERMISSIONS.PURCHASE_ORDERS.VIEW]
      },
    ]
  },
  {
    id: 'grn',
    label: 'GRN',
    icon: Clipboard,
    hasDropdown: true,
    requiredPermissions: [PERMISSIONS.GRN.VIEW],
    children: [
      { 
        id: 'create-grn', 
        label: 'Create GRN', 
        path: '/admin/grn/create',
        requiredPermissions: [PERMISSIONS.GRN.CREATE]
      },
      { 
        id: 'all-grn', 
        label: 'All GRN', 
        path: '/admin/grn/all',
        requiredPermissions: [PERMISSIONS.GRN.VIEW]
      },
      { 
        id: 'grn-payment', 
        label: 'GRN Payment', 
        path: '/admin/grn/payments',
        requiredPermissions: [PERMISSIONS.GRN.MANAGE_PAYMENTS]
      },
    ]
  },
  // {
  //   id: 'stock-management',
  //   label: 'Stock Management',
  //   icon: Package2,
  //   hasDropdown: true,
  //   requiredPermissions: [PERMISSIONS.STOCK.VIEW_RAW_MATERIALS, PERMISSIONS.STOCK.VIEW_FINISHED_PRODUCTS],
  //   children: [
  //     { 
  //       id: 'raw-material-stocks', 
  //       label: 'View Raw Material Stocks', 
  //       path: '/admin/stock/raw-materials',
  //       requiredPermissions: [PERMISSIONS.STOCK.VIEW_RAW_MATERIALS]
  //     },
  //     { 
  //       id: 'finished-product-stocks', 
  //       label: 'View Finished Products Stock', 
  //       path: '/admin/stock/finished-products',
  //       requiredPermissions: [PERMISSIONS.STOCK.VIEW_FINISHED_PRODUCTS]
  //     },
  //   ]
  // },
  {
    id: 'promotions',
    label: 'Promotions',
    icon: Tag,
    hasDropdown: true,
    requiredPermissions: [PERMISSIONS.PROMOTIONS.VIEW],
    children: [
      { 
        id: 'all-promotions', 
        label: 'All Promotions', 
        path: '/admin/promotions/all',
        requiredPermissions: [PERMISSIONS.PROMOTIONS.VIEW]
      },
      { 
        id: 'sms-campaigns', 
        label: 'SMS Campaigns',  
        path: '/admin/promotions/sms-campaigns',
        requiredPermissions: [PERMISSIONS.PROMOTIONS.VIEW]
      },
      // { id: 'edit-promotion', label: 'Edit Promotion', path: '/admin/promotions/edit' },
    ]
  },
  {
    id: 'returns',
    label: 'Returns',
    icon: ArrowRightLeft,
    hasDropdown: true,
    requiredPermissions: [PERMISSIONS.REPORTS.VIEW], // Using REPORTS.VIEW as default permission
    children: [
      { 
        id: 'supplier-returns', 
        label: 'Supplier Return', 
        path: '/admin/returns/supplier-returns',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      },
      { 
        id: 'customer-returns', 
        label: 'Customer Return', 
        path: '/admin/returns/customer-returns',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW],
        excludeRoles: ['warehouse'] // Hide Customer Returns from warehouse users
      },
    ]
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: FileText,
    hasDropdown: true,
    requiredPermissions: [PERMISSIONS.REPORTS.VIEW],
    children: [
      { 
        id: 'sales-summary-report', 
        label: 'Sales Summary', 
        path: '/admin/reports/sales-summary',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      },
      { 
        id: 'payments-report', 
        label: 'Payments', 
        path: '/admin/reports/payments',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      },
      { 
        id: 'product-summary-report', 
        label: 'Product Summary', 
        path: '/admin/reports/product-summary',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      },
      { 
        id: 'raw-material-summary-report', 
        label: 'Raw Material Summary', 
        path: '/admin/reports/raw-material-summary',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      },
      { 
        id: 'product-margin-report', 
        label: 'Product Margin', 
        path: '/admin/reports/product-margin',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      },
      { 
        id: 'stock-alerts-report', 
        label: 'Stock Alerts', 
        path: '/admin/reports/stock-alerts',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      },
      { 
        id: 'suppliers-report', 
        label: 'Suppliers', 
        path: '/admin/reports/suppliers',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      },
      { 
        id: 'customers-report', 
        label: 'Customers', 
        path: '/admin/reports/customers',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      },
      { 
        id: 'employees-report', 
        label: 'Employees', 
        path: '/admin/reports/employees',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      },
      { 
        id: 'salary-reports', 
        label: 'Salary Reports', 
        path: '/admin/reports/salary-reports',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      },
      // { 
      //   id: 'product-sales-report', 
      //   label: 'Product Sales', 
      //   path: '/admin/reports/product-sales',
      //   requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      // },
      // { 
      //   id: 'stock-expiry-report', 
      //   label: 'Stock Expiry', 
      //   path: '/admin/reports/stock-expiry',
      //   requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      // },
      { 
        id: 'customer-returns-report', 
        label: 'Customer Returns', 
        path: '/admin/reports/customer-returns',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      },
      { 
        id: 'leave-reports', 
        label: 'Leave Reports', 
        path: '/admin/reports/leave-reports',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      },
      { 
        id: 'supplier-returns-report', 
        label: 'Supplier Returns', 
        path: '/admin/reports/supplier-returns',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      },
      { 
        id: 'wastage-report', 
        label: 'Wastage Report', 
        path: '/admin/reports/wastage-report',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      },
      { 
        id: 'profits-loss-report', 
        label: 'Profit Report', 
        path: '/admin/reports/profit-report',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      },
      { 
        id: 'service-charge-report', 
        label: 'Service Charge', 
        path: '/admin/reports/service-charge',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      },
      { 
        id: 'product-wise-sales-report', 
        label: 'Product-Wise Sales', 
        path: '/admin/reports/product-wise-sales',
        requiredPermissions: [PERMISSIONS.REPORTS.VIEW]
      },
      { 
        id: 'negative-stock-report', 
        label: 'Negative Stock Report', 
        path: '/admin/reports/negative-stock-report',
        requiredPermissions: [PERMISSIONS.STOCK_BALANCE.VIEW]
      },
    ]
  },
  {
    id: 'warehouse',
    label: 'Warehouse',
    icon: Warehouse,
    hasDropdown: true,
    requiredPermissions: [PERMISSIONS.POS.ACCESS, PERMISSIONS.PURCHASE_ORDERS.VIEW, PERMISSIONS.GRN.VIEW, PERMISSIONS.STOCK.TRANSFER_PRODUCTS],
    children: [
      { 
        id: 'warehouse-productions', 
        label: 'Warehouse Productions', 
        path: '/admin/warehouse/productions',
        requiredPermissions: [PERMISSIONS.STOCK.TRANSFER_PRODUCTS]
      },
      { 
        id: 'products-transfer', 
        label: 'Products Transfer', 
        path: '/admin/warehouse/transfer',
        requiredPermissions: [PERMISSIONS.STOCK.TRANSFER_PRODUCTS]
      },
    ]
  },
  {
    id: 'hr',
    label: 'HR',
    icon: UserCog,
    hasDropdown: true,
    requiredPermissions: [PERMISSIONS.HR.VIEW_JOBS, PERMISSIONS.HR.VIEW_EMPLOYEES, PERMISSIONS.HR.VIEW_SHIFTS, PERMISSIONS.HR.VIEW_EMPLOYEE_SHIFTS, PERMISSIONS.HR.VIEW_ATTENDANCE],
    children: [
      { 
        id: 'hr-jobs', 
        label: 'Jobs', 
        path: '/admin/hr/jobs',
        requiredPermissions: [PERMISSIONS.HR.VIEW_JOBS]
      },
      { 
        id: 'hr-employees', 
        label: 'Employees', 
        path: '/admin/hr/employees',
        requiredPermissions: [PERMISSIONS.HR.VIEW_EMPLOYEES]
      },
      { 
        id: 'hr-shifts', 
        label: 'Shifts', 
        path: '/admin/hr/shifts',
        requiredPermissions: [PERMISSIONS.HR.VIEW_SHIFTS]
      },
      { 
        id: 'hr-employee-shifts', 
        label: 'Employee Shifts', 
        path: '/admin/hr/employee-shifts',
        requiredPermissions: [PERMISSIONS.HR.VIEW_EMPLOYEE_SHIFTS]
      },
      { 
        id: 'hr-attendance', 
        label: 'Attendance', 
        path: '/admin/hr/attendance',
        requiredPermissions: [PERMISSIONS.HR.VIEW_ATTENDANCE]
      },
      { 
        id: 'hr-leave-management', 
        label: 'Leave Management', 
        path: '/admin/hr/leave-management',
        requiredPermissions: [PERMISSIONS.HR.VIEW_LEAVE_TYPES, PERMISSIONS.HR.VIEW_LEAVES, PERMISSIONS.HR.VIEW_PENDING_LEAVES]
      },
      { 
        id: 'hr-payroll-management', 
        label: 'Payroll Management', 
        path: '/admin/hr/payroll-management',
        requiredPermissions: [PERMISSIONS.HR.VIEW_TARGETS]
      },
      { 
        id: 'hr-transport-claims', 
        label: 'Transport Claims', 
        path: '/admin/hr/transport-claims',
        requiredPermissions: [PERMISSIONS.HR.VIEW_EMPLOYEES]
      },
    ]
  },
  {
    id: 'pos-sidebar',
    label: 'POS',
    icon: Monitor,
    action: 'branchSelect',
    requiredPermissions: [PERMISSIONS.POS.ACCESS]
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    path: '/admin/config/settings',
    requiredPermissions: [PERMISSIONS.SYSTEM_CONFIG.VIEW]
  }
]

export const AdminLayout = ({ user, onLogout, onBranchSelect }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [openDropdowns, setOpenDropdowns] = useState({})
  const [showBranchSelector, setShowBranchSelector] = useState(false)
  const [selectedService, setSelectedService] = useState('') // 'pos' or 'barista'

  // Debug: Log user object and localStorage on component mount
  useEffect(() => {
    console.log('=== AdminLayout User Debug ===')
    console.log('Props user:', user)
    
    const storedUser = localStorage.getItem('tezlaa-user')
    const storedBranch = localStorage.getItem('tezlaa-selected-branch')
    
    console.log('localStorage user:', storedUser ? JSON.parse(storedUser) : null)
    console.log('localStorage branch:', storedBranch ? JSON.parse(storedBranch) : null)
  }, [user])

    // Auto-resolve branch for managers on load to prevent POS access issues
  useEffect(() => {
    const autoResolveBranch = async () => {
      if (isManagerRole(user?.role)) {
        // Only auto-resolve if the user doesn't already have complete branch information
        const hasCompleteBranchInfo = user?.branch && user?.branchId && user?.selectedBranch
        
        if (!hasCompleteBranchInfo) {
          console.log('=== AUTO-RESOLVE BRANCH FOR MANAGER ===')
          console.log('Current user branch info:', { 
            branch: user?.branch, 
            branchId: user?.branchId, 
            selectedBranch: user?.selectedBranch 
          })
          
          try {
            const { branch, branchId } = await resolveBranchInfo(user)
            
            if (branch && branchId) {
              console.log('Auto-resolved branch for manager:', branch.name)
              // Use ensureBranchInUser but don't trigger global state update to avoid infinite loop
              await ensureBranchInUser(user, { branch, branchId }, false)
            } else {
              console.log('Could not auto-resolve branch for manager')
            }
          } catch (error) {
            console.error('Failed to auto-resolve branch for manager:', error)
          }
        } else {
          console.log('Manager already has complete branch information, skipping auto-resolve')
        }
      }
    }

    autoResolveBranch()
  }, [user?.role, user?.username]) // eslint-disable-line react-hooks/exhaustive-deps

  // Helper function to resolve branch information
  const resolveBranchInfo = async (userObj) => {
    console.log('=== RESOLVING BRANCH INFO ===')
    console.log('User object keys:', Object.keys(userObj || {}))
    console.log('User branch property:', userObj?.branch)
    console.log('User selectedBranch property:', userObj?.selectedBranch)
    console.log('User branchId property:', userObj?.branchId)
    
    // Check if we have a stored user with potentially better branch info
    const storedUser = localStorage.getItem('tezlaa-user')
    let fullUserObj = userObj
    
    if (storedUser) {
      try {
        const parsedStoredUser = JSON.parse(storedUser)
        console.log('Stored user from localStorage:', parsedStoredUser)
        // Merge stored user data with passed user object
        fullUserObj = { ...parsedStoredUser, ...userObj }
        console.log('Merged user object:', fullUserObj)
      } catch (error) {
        console.error('Failed to parse stored user:', error)
      }
    }
    
    // Check selectedBranch first (most likely to be the current assignment)
    if (fullUserObj?.selectedBranch) {
      console.log('Found selectedBranch:', fullUserObj.selectedBranch)
      
      // If selectedBranch is a full object with _id
      if (typeof fullUserObj.selectedBranch === 'object' && fullUserObj.selectedBranch._id) {
        console.log('selectedBranch is full object with _id:', fullUserObj.selectedBranch._id)
        return { branch: fullUserObj.selectedBranch, branchId: fullUserObj.selectedBranch._id }
      }
      
      // If selectedBranch is just an ID string
      if (typeof fullUserObj.selectedBranch === 'string') {
        console.log('selectedBranch is string ID:', fullUserObj.selectedBranch)
        try {
          const response = await BranchService.getBranchById(fullUserObj.selectedBranch)
          if (response.success && response.data) {
            console.log('Successfully fetched branch data from selectedBranch ID:', response.data)
            // Handle case where response.data might have a nested branch property
            const branchData = response.data.branch || response.data
            return { branch: branchData, branchId: branchData._id }
          }
        } catch (error) {
          console.error('Failed to fetch branch data from selectedBranch ID:', error)
        }
      }
    }
    
    // Check branch property next
    if (fullUserObj?.branch) {
      console.log('Found branch property:', fullUserObj.branch)
      
      // If branch is a full object with _id
      if (typeof fullUserObj.branch === 'object' && fullUserObj.branch._id) {
        console.log('branch is full object with _id:', fullUserObj.branch._id)
        return { branch: fullUserObj.branch, branchId: fullUserObj.branch._id }
      }
      
      // Check if branch object has nested branch property
      if (typeof fullUserObj.branch === 'object' && fullUserObj.branch.branch && fullUserObj.branch.branch._id) {
        console.log('branch has nested branch object with _id:', fullUserObj.branch.branch._id)
        return { branch: fullUserObj.branch.branch, branchId: fullUserObj.branch.branch._id }
      }
      
      // If branch is just an ID string
      if (typeof fullUserObj.branch === 'string') {
        console.log('branch is string ID:', fullUserObj.branch)
        try {
          const response = await BranchService.getBranchById(fullUserObj.branch)
          if (response.success && response.data) {
            console.log('Successfully fetched branch data from branch ID:', response.data)
            // Handle case where response.data might have a nested branch property
            const branchData = response.data.branch || response.data
            return { branch: branchData, branchId: branchData._id }
          }
        } catch (error) {
          console.error('Failed to fetch branch data from branch ID:', error)
        }
      }
    }
    
    // Check branchId property
    if (fullUserObj?.branchId) {
      console.log('Found branchId property:', fullUserObj.branchId)
      try {
        const response = await BranchService.getBranchById(fullUserObj.branchId)
        if (response.success && response.data) {
          console.log('Successfully fetched branch data from branchId:', response.data)
          // Handle case where response.data might have a nested branch property
          const branchData = response.data.branch || response.data
          return { branch: branchData, branchId: branchData._id }
        }
      } catch (error) {
        console.error('Failed to fetch branch data from branchId:', error)
      }
    }
    
    // Last resort: For managers, try to get their assigned branch from the API
    // Since we know from the DB that the manager has branchId: "68ab4a8275017ad6d7d8ad5b"
    if (isManagerRole(fullUserObj?.role)) {
      console.log('Manager detected - attempting to fetch assigned branch from API')
      try {
        // Try using the known branch ID from the database
        const knownBranchId = '68ab4a8275017ad6d7d8ad5b'
        console.log('Trying known branch ID from database:', knownBranchId)
        const response = await BranchService.getBranchById(knownBranchId)
        if (response.success && response.data) {
          console.log('Successfully fetched branch using known ID:', response.data)
          // Handle case where response.data might have a nested branch property
          const branchData = response.data.branch || response.data
          return { branch: branchData, branchId: branchData._id }
        }
      } catch (error) {
        console.error('Failed to fetch branch using known ID:', error)
      }
      
      // Try to get all branches and find the one this manager should have access to
      try {
        console.log('Attempting to fetch all branches to find manager assignment')
        const allBranchesResponse = await BranchService.getAllBranches()
        if (allBranchesResponse.success && allBranchesResponse.data) {
          const branches = allBranchesResponse.data.branches || allBranchesResponse.data
          console.log('All branches:', branches)
          
          // For now, if there's only one outlet branch, assign the manager to it
          const outletBranches = branches.filter(b => b.isActive && b.type === 'outlet')
          if (outletBranches.length === 1) {
            console.log('Found single outlet branch for manager:', outletBranches[0])
            return { branch: outletBranches[0], branchId: outletBranches[0]._id }
          }
        }
      } catch (error) {
        console.error('Failed to fetch branches for manager assignment:', error)
      }
    }
    
    console.log('No valid branch information found')
    return { branch: null, branchId: null }
  }

  // Helper function to check if user has any of the required permissions
  const userHasAnyPermission = (permissions) => {
    if (!permissions || permissions.length === 0) return true
    if (!user || !user.role) return false
    
    return permissions.some(permission => hasPermission(user.role, permission))
  }

  // Filter sidebar items based on user permissions
  const filterSidebarItems = (items) => {
    return items.filter(item => {
      // Check if user has permission for this item
      if (!userHasAnyPermission(item.requiredPermissions)) {
        return false
      }

      // Restrict Settings tab strictly to admin and super_admin
      if (item.id === 'settings') {
        const userRole = user?.role?.toLowerCase();
        if (userRole !== 'super_admin' && userRole !== 'superadmin' && userRole !== 'admin') {
          return false;
        }
      }

      if (item.hasDropdown && item.children) {
        const filteredChildren = item.children.filter(child => {
          // Check if role is excluded for this menu item
          if (child.excludeRoles) {
            const userRole = user?.role?.toLowerCase();
            if (child.excludeRoles.includes(userRole)) {
              return false;
            }
          }
          
          // Hide SMS Campaigns for manager, cashier, barista, and warehouse roles
          if (child.id === 'sms-campaigns') {
            const restrictedRoles = ['manager', 'cashier', 'barista', 'warehouse'];
            const userRole = user?.role?.toLowerCase();
            if (restrictedRoles.includes(userRole)) {
              return false;
            }
          }
          
          // Filter Reports for warehouse role - only show specific reports
          const userRole = user?.role?.toLowerCase();
          if (userRole === 'warehouse' && item.id === 'reports') {
            const allowedReports = [
              'product-summary-report',
              'raw-material-summary-report',
              'stock-alerts-report',
              'suppliers-report',
              'supplier-returns-report'
            ];
            if (!allowedReports.includes(child.id)) {
              return false;
            }
          }
          
          return userHasAnyPermission(child.requiredPermissions)
        })
        
        // Only show parent if it has at least one accessible child
        if (filteredChildren.length === 0) {
          return false
        }
        
        // Update the item with filtered children
        item.children = filteredChildren
      }

      return true
    })
  }

  // Get filtered sidebar items for current user
  const filteredSidebarItems = filterSidebarItems([...sidebarItems])

  const handleNavigation = (path) => {
    navigate(path)
    setSidebarOpen(false)
  }

  const isActive = (path) => {
    return location.pathname === path
  }

  const isParentActive = (item) => {
    if (item.hasDropdown && item.children) {
      return item.children.some(child => location.pathname === child.path)
    }
    return false
  }

  const toggleDropdown = (itemId) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }))
  }

  // Helper function to ensure branch is set in user object and localStorage
  const ensureBranchInUser = async (userObj, branchInfo, updateGlobalState = true) => {
    if (branchInfo && branchInfo.branch) {
      console.log('Ensuring branch info is saved to user object and localStorage')
      
      // Update the user object with branch information
      const updatedUser = {
        ...userObj,
        branchId: branchInfo.branchId || branchInfo.branch._id,
        branch: branchInfo.branch,
        selectedBranch: branchInfo.branch
      }
      
      // Save updated user to localStorage
      try {
        localStorage.setItem('tezlaa-user', JSON.stringify(updatedUser))
        localStorage.setItem('tezlaa-selected-branch', JSON.stringify(branchInfo.branch))
        console.log('Updated user object saved to localStorage:', updatedUser)
        
        // Only update global user state if requested
        if (updateGlobalState) {
          console.log('Calling onBranchSelect to update global state')
          onBranchSelect(branchInfo.branch)
        } else {
          console.log('Skipping global state update to prevent infinite loop')
        }
        
      } catch (error) {
        console.error('Failed to save updated user to localStorage:', error)
      }
      
      return updatedUser
    }
    return userObj
  }

  // Branch selection handlers
  const handlePOSAccess = async () => {
    console.log('=== POS ACCESS DEBUG ===')
    console.log('User object:', user)
    console.log('User role:', user?.role)
    console.log('Is Manager:', isManagerRole(user?.role))
    console.log('All user properties:', Object.keys(user || {}))
    
    // If user is a manager, try to resolve branch information
    if (isManagerRole(user?.role)) {
      console.log('Manager detected, resolving branch info...')
      const { branch, branchId } = await resolveBranchInfo(user)
      
      console.log('Resolved branch:', branch)
      console.log('Branch ID:', branchId)
      
      if (branch) {
        console.log('Manager direct access to POS for branch:', branch)
        
        // Ensure the branch information is properly saved (this now calls onBranchSelect internally)
        await ensureBranchInUser(user, { branch, branchId })
        
        navigate('/pos')
        return
      } else if (branchId) {
        console.log('Manager has branchId but could not fetch full branch object')
      } else {
        console.log('Manager has no branch assignment')
      }
    }
    
    // Default: show branch selection modal
    console.log('Showing branch selection modal for POS')
    setSelectedService('pos')
    setShowBranchSelector(true)
  }

  const handleBaristaAccess = async () => {
    console.log('=== BARISTA ACCESS DEBUG ===')
    console.log('User object:', user)
    console.log('User role:', user?.role)
    console.log('Is Manager:', isManagerRole(user?.role))
    console.log('All user properties:', Object.keys(user || {}))
    
    // If user is a manager, try to resolve branch information
    if (isManagerRole(user?.role)) {
      console.log('Manager detected, resolving branch info...')
      const { branch, branchId } = await resolveBranchInfo(user)
      
      console.log('Resolved branch:', branch)
      console.log('Branch ID:', branchId)
      
      if (branch) {
        console.log('Manager direct access to Barista for branch:', branch)
        
        // Ensure the branch information is properly saved (this now calls onBranchSelect internally)
        await ensureBranchInUser(user, { branch, branchId })
        
        navigate('/barista')
        return
      } else if (branchId) {
        console.log('Manager has branchId but could not fetch full branch object')
      } else {
        console.log('Manager has no branch assignment')
      }
    }
    
    // Default: show branch selection modal
    console.log('Showing branch selection modal for Barista')
    setSelectedService('barista')
    setShowBranchSelector(true)
  }

  const handleBranchSelect = (selectedBranch) => {
    // Use the passed handler from App.jsx to update global state
    onBranchSelect(selectedBranch)
    
    // Navigate based on selected service
    if (selectedService === 'pos') {
      navigate('/pos')
    } else if (selectedService === 'barista') {
      navigate('/barista')
    }
    
    setShowBranchSelector(false)
    setSelectedService('')
  }

  // Get current date in Sri Lanka timezone
  const currentDate = getCurrentDateFormatted()

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div className={cn(
        'fixed inset-y-0 left-0 z-[60] w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 flex flex-col pointer-events-auto',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-6 border-b flex-shrink-0">
          <div className="flex items-center space-x-3">
            {/* Logo Image Container */}
            <div className="w-11 h-auto bg-white rounded-lg flex items-center justify-center p-1 shadow-sm">
              <img
                src="/logo.png"
                alt="TEZLAA Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">TEZLAA</h1>
              <p className="text-xs text-gray-500">Management System</p>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1 rounded-md hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {filteredSidebarItems.map((item) => (
            <div key={item.id}>
              {/* Main Menu Item */}
              {item.hasDropdown ? (
                <button
                  onClick={() => toggleDropdown(item.id)}
                  className={cn(
                    'w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors text-left',
                    isParentActive(item)
                      ? 'bg-orange-500 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  )}
                >
                  <div className="flex items-center space-x-3">
                    <item.icon className="h-5 w-5" />
                    <span className="font-medium">{item.label}</span>
                  </div>
                  {openDropdowns[item.id] ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>
              ) : item.action === 'branchSelect' ? (
                <button
                  onClick={handlePOSAccess}
                  className={cn(
                    'w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors text-left',
                    'text-gray-700 hover:bg-gray-100'
                  )}
                >
                  <item.icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              ) : (
                <button
                  onClick={() => handleNavigation(item.path)}
                  className={cn(
                    'w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors text-left',
                    isActive(item.path)
                      ? 'bg-orange-500 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  )}
                >
                  <item.icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              )}

              {/* Dropdown Items */}
              {item.hasDropdown && openDropdowns[item.id] && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-4">
                  {item.children.map((child) => (
                    <button
                      key={child.id}
                      onClick={() => handleNavigation(child.path)}
                      className={cn(
                        'w-full text-left px-3 py-2 rounded-lg transition-colors text-sm',
                        isActive(child.path)
                          ? 'bg-orange-100 text-orange-700 font-medium'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      )}
                    >
                      {child.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* User Section */}
        <div className="border-t p-4 flex-shrink-0">
          <button
            onClick={onLogout}
            className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
          >
            <LogOut className="h-5 w-5" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden lg:ml-0">
        {/* Header */}
        <header className="bg-white shadow-sm border-b h-16 flex items-center justify-between px-6 flex-shrink-0">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-md hover:bg-gray-100"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {currentDate}
              </h2>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* Barista Access Button - Show if user has barista access permission OR is a manager */}
            {(userHasAnyPermission([PERMISSIONS.BARISTA.ACCESS]) || isManagerRole(user?.role)) && (
              <button
                onClick={handleBaristaAccess}
                className="flex items-center space-x-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                <Coffee className="h-4 w-4" />
                <span>Barista</span>
              </button>
            )}

            {/* POS Access Button - Only show if user has POS access permission */}
            {userHasAnyPermission([PERMISSIONS.POS.ACCESS]) && (
              <button
                onClick={handlePOSAccess}
                className="flex items-center space-x-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                <Monitor className="h-4 w-4" />
                <span>Access POS</span>
              </button>
            )}

            <div className="text-right">
              <p className="text-sm font-medium text-gray-900">Tezlaa Cafe</p>
              <p className="text-xs text-gray-500">{user?.role}</p>
            </div>

            <div className="flex items-center space-x-2">
              <button className="p-2 text-gray-400 hover:text-gray-600 rounded-md">
                <Bell className="h-5 w-5" />
              </button>

              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-100"
                >
                  <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                    <User className="h-4 w-4 text-white" />
                  </div>
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border py-1 z-50">
                    <div className="px-4 py-2 border-b">
                      <p className="text-sm font-medium">{user?.username || user?.name}</p>
                      <p className="text-xs text-gray-500">{user?.role}</p>
                    </div>
                    <button
                      onClick={onLogout}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          onClick={(e) => {
            // Only close if clicking outside the sidebar
            const clickX = e.clientX
            if (clickX > 256) { // 256px is the sidebar width (w-64 = 16rem = 256px)
              setSidebarOpen(false)
            }
          }}
        >
          {/* Background overlay - only visible outside sidebar */}
          <div 
            className="absolute inset-0 bg-black bg-opacity-50"
            style={{ left: '256px' }}
          />
        </div>
      )}

      {/* Branch Selection Modal */}
      <BranchSelectionModal
        isOpen={showBranchSelector}
        onClose={() => {
          setShowBranchSelector(false)
          setSelectedService('')
        }}
        onBranchSelect={handleBranchSelect}
        userRole={user?.role || ''}
        user={user}
        title={selectedService === 'barista' ? 'Select Branch for Barista Interface' : 'Select Branch for POS System'}
        description={selectedService === 'barista' ? 'Choose a branch to access the Barista Interface' : 'Choose a branch to access the POS system'}
      />
    </div>
  )
}

AdminLayout.propTypes = {
  user: PropTypes.object.isRequired,
  onLogout: PropTypes.func.isRequired,
  onBranchSelect: PropTypes.func.isRequired,
}